import { useState } from 'react';
import { uploadTempImage } from '../services/storage.service.v2';
import { createPendingPattern } from '../services/pendingPattern.service';
import { callGeneratePattern } from '../services/pattern.service';
import { trackError, ERROR_CONTEXTS } from '../services/analytics.service';

/**
 * Random ID generator for pattern IDs
 */
const generatePatternId = () => {
	const chars =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let result = '';
	for (let i = 0; i < 32; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return result;
};

/**
 * Hook for orchestrating the pattern generation flow (subscribed users only)
 * Handles: image upload → pending pattern creation → CF generation → pattern ready
 *
 * @param {object} user - Firebase user object
 * @returns {object}
 *   - generatePattern: function to initiate generation
 *   - generating: boolean indicating generation in progress
 *   - patternURL: download URL when pattern is ready
 *   - error: error message if generation failed
 */
export const useGeneratePattern = (user) => {
	const [generating, setGenerating] = useState(false);
	const [patternURL, setPatternURL] = useState(null);
	const [error, setError] = useState(null);

	const generatePattern = async ({
		type, // 'library' | 'user'
		fileName, // library primary image base name
		fileName2, // library secondary image base name (lenticular)
		imagePrimaryDataURL, // user-uploaded primary image data URL
		imageSecondaryDataURL, // user-uploaded secondary image data URL (lenticular)
		bookParams, // { height, lastOddPage, angleLine, isInkSaver, measurementUnit, extraFrontPages, extraBackPages }
	}) => {
		if (!user || !user.uid) {
			setError('User must be authenticated');
			return;
		}

		setGenerating(true);
		setError(null);
		setPatternURL(null);

		try {
			// Step 1: Generate pattern ID
			const patternId = generatePatternId();

			// Step 2: Upload images if user-uploaded pattern
			let tempImagePath = null;
			let tempImagePath2 = null;

			if (type === 'user') {
				const primaryUpload = await uploadTempImage(
					user.uid,
					patternId,
					imagePrimaryDataURL,
				);
				tempImagePath = primaryUpload.path;

				if (imageSecondaryDataURL) {
					const secondaryUpload = await uploadTempImage(
						user.uid,
						patternId,
						imageSecondaryDataURL,
						'-2',
					);
					tempImagePath2 = secondaryUpload.path;
				}
			}

			// Step 3: Create pending pattern document
			await createPendingPattern(user.uid, patternId, {
				type,
				fileName: fileName || null,
				fileName2: fileName2 || null,
				tempImagePath,
				tempImagePath2,
				bookParams,
				// Note: no priceId or productId for subscribed users
			});

			// Step 4: Call Cloud Function to generate pattern
			const result = await callGeneratePattern(patternId, null);

			// Step 5: Set download URL
			setPatternURL(result.downloadURL);
			setGenerating(false);
		} catch (err) {
			trackError(ERROR_CONTEXTS.USE_GENERATE_PATTERN, err);
			setError(err.message || 'Failed to generate pattern');
			setGenerating(false);
		}
	};

	return {
		generatePattern,
		generating,
		patternURL,
		error,
	};
};
